import { useCallback } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";

import { supabase } from "@/integrations/supabase/client";

/**
 * Persistent "Saved organizations" set for the current agent.
 *
 * Modeled on useOrgsToFollow: a single cached query is the source of truth for
 * the saved org_ids, hydrated on mount so every Save control — the Explore card
 * bookmark, the list-row bookmark, the map popup, the Quick View modal, and the
 * profile Save button — renders the real saved state after a reload or client
 * navigation, not just within the session.
 *
 * `saved_organizations` is the Save bookmark set, deliberately distinct from
 * `org_follows` (the Follow concept that drives the feed right rail). The table
 * is created by a manually-applied migration and is absent from the generated
 * Supabase types, so the table name / insert payload are cast `as any` — the same
 * pattern useFeedPosts/FeedCard use for feed_comment_likes.
 */

// Postgres unique_violation: the (user_id, org_id) bookmark already exists.
const UNIQUE_VIOLATION = "23505";

const savedOrgsKey = (userId?: string) => ["saved-orgs", userId] as const;

export function useSavedOrgs(userId?: string) {
  const queryClient = useQueryClient();
  const queryKey = savedOrgsKey(userId);

  // Hydrate the current user's saved org_ids on mount; cached so toggling from
  // any surface flips in place rather than refetching the whole set each time.
  const { data: savedOrgIds = [], isLoading, isError, refetch } = useQuery({
    queryKey,
    enabled: !!userId,
    staleTime: 5 * 60 * 1000,
    queryFn: async (): Promise<string[]> => {
      const { data, error } = await supabase
        .from("saved_organizations" as any)
        .select("org_id")
        .eq("user_id", userId!);
      // Throw rather than cache an empty set: a cached [] would show every saved
      // org as unsaved for the whole staleTime, and the next Save would insert a
      // duplicate. Erroring lets React Query retry and keeps "unknown" distinct
      // from "none saved".
      if (error) throw error;
      if (!data) throw new Error("Saved organizations read returned no data");
      return (data as Array<{ org_id: string }>).map((r) => r.org_id);
    },
  });

  const { mutate } = useMutation({
    mutationFn: async ({
      orgId,
      nextSaved,
    }: {
      orgId: string;
      name?: string;
      nextSaved: boolean;
    }) => {
      if (!userId) throw new Error("Not authenticated");
      if (nextSaved) {
        const { error } = await supabase
          .from("saved_organizations" as any)
          .insert({ user_id: userId, org_id: orgId } as any);
        // Already saved is the state the agent asked for, not a failure;
        // onSettled reconciles the cache with the server.
        if (error && error.code !== UNIQUE_VIOLATION) throw error;
      } else {
        const { error } = await supabase
          .from("saved_organizations" as any)
          .delete()
          .eq("user_id", userId)
          .eq("org_id", orgId);
        if (error) throw error;
      }
    },
    // Optimistic: flip the cached set immediately so the bookmark responds
    // without waiting on the round-trip; snapshot for rollback on error.
    onMutate: async ({ orgId, nextSaved }) => {
      await queryClient.cancelQueries({ queryKey });
      const previous = queryClient.getQueryData<string[]>(queryKey) ?? [];
      queryClient.setQueryData<string[]>(
        queryKey,
        nextSaved ? [...previous, orgId] : previous.filter((id) => id !== orgId),
      );
      return { previous };
    },
    onError: (_err, _vars, context) => {
      if (context?.previous) queryClient.setQueryData(queryKey, context.previous);
      toast.error("Couldn't update your saved organizations.");
    },
    onSuccess: (_data, { name, nextSaved }) => {
      if (nextSaved) toast.success(name ? `Saved ${name}` : "Organization saved");
      else toast(name ? `Removed ${name} from saved` : "Removed from saved");
    },
    // Reconcile with the server once the write settles.
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey });
    },
  });

  const toggleSave = useCallback(
    (orgId: string, name?: string) => {
      if (!userId) return; // not signed in — nothing to persist
      const nextSaved = !savedOrgIds.includes(orgId);
      mutate({ orgId, name, nextSaved });
    },
    [userId, savedOrgIds, mutate],
  );

  // isError/refetch let a list surface tell a failed read from "none saved"
  // (#1242); savedOrgIds is [] in both cases.
  return { savedOrgIds, isLoading, isError, refetch, toggleSave };
}
